import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { CheckCircle, ErrorOutline, Refresh } from '@mui/icons-material';
import financeService from '../../../services/financeService';
import { CloseChecklistItem } from '../../../types/finance';
import ConfirmationDialog from '../../common/ConfirmationDialog';

/**
 * Closing a month: what must hold first, checked on the server. The month
 * closes only when every item passes; fix one elsewhere and check again.
 */
const CloseChecklistDialog: React.FC<{
  periodId: string | null;
  monthLabel: string;
  onClose: () => void;
  onClosed: () => void;
}> = ({ periodId, monthLabel, onClose, onClosed }) => {
  const [items, setItems] = useState<CloseChecklistItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!periodId) return;
    setChecking(true);
    try {
      setItems(await financeService.getCloseChecklist(periodId));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not check the month.');
    } finally {
      setChecking(false);
    }
  }, [periodId]);

  useEffect(() => { setItems(null); setError(null); check(); }, [check]);

  const close = async () => {
    setBusy(true);
    try {
      await financeService.changePeriod(periodId!, 'close');
      onClosed();
    } catch (e: any) {
      setError(e?.message || 'The month could not close.');
      await check();
    } finally {
      setBusy(false);
    }
  };

  const failing = items?.filter(i => !i.ok).length ?? 0;

  return (
    <ConfirmationDialog
      open={!!periodId}
      onClose={onClose}
      onConfirm={close}
      loading={busy}
      confirmDisabled={!items || failing > 0 || checking}
      title={`Close ${monthLabel}?`}
      message="Once closed, the month is sealed: only people who manage fiscal periods can post into it, and every such posting is logged."
      confirmText="Close month"
      confirmColor="primary"
    >
      <Box sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {!items ? [0, 1, 2, 3].map(i => <Skeleton key={i} height={52} sx={{ mx: 1.75 }} />) : items.map(item => (
          <Stack
            key={item.key}
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{ px: 1.75, py: 1.1, '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}
          >
            {item.ok
              ? <CheckCircle fontSize="small" sx={{ color: 'success.main', mt: 0.15 }} />
              : <ErrorOutline fontSize="small" sx={{ color: 'error.main', mt: 0.15 }} />}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
              <Typography variant="caption" color={item.ok ? 'text.secondary' : 'error.dark'}>{item.detail}</Typography>
            </Box>
          </Stack>
        ))}
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.25 }}>
        <Typography variant="caption" color="text.secondary">
          {items && (failing ? `${failing} item${failing === 1 ? '' : 's'} to resolve before closing.` : 'Everything checks out.')}
        </Typography>
        <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={check} disabled={checking || busy}>Check again</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
    </ConfirmationDialog>
  );
};

export default CloseChecklistDialog;
